import { devWarn } from "@/util/dev.mjs";

export type ClassNameEntry =
  | string
  | boolean
  | (() => string)
  | null
  | number
  | undefined;

type ValidClassNameEntry = string | (() => string);

// Helper type to check if at least one valid entry exists
type HasValidEntry<T extends ClassNameEntry[]> = T extends []
  ? false
  : T extends [infer First, ...infer Rest]
    ? First extends ValidClassNameEntry
      ? First extends string
        ? First extends ""
          ? Rest extends ClassNameEntry[]
            ? HasValidEntry<Rest>
            : false
          : true
        : true
      : Rest extends ClassNameEntry[]
        ? HasValidEntry<Rest>
        : false
    : false;

// Conditional return type based on arguments
type ClassNamesReturn<T extends ClassNameEntry[]> =
  HasValidEntry<T> extends true ? { className: string } : Record<string, never>;

// filters nullish and falsy values so you don't have to return "" for falsey
export function classNames<T extends ClassNameEntry[]>(
  ...args: T
): ClassNamesReturn<T> {
  const classNames = args.reduce(resolveClassNames, [] as string[]);

  // Only return className property if we have valid class names
  if (classNames.length > 0) {
    return {
      className: classNames.join(" "),
    } as ClassNamesReturn<T>;
  }

  // Return empty object when no valid entries
  return {} as Record<string, never> as ClassNamesReturn<T>;
}

function resolveClassNames(acc: string[], entry: ClassNameEntry): string[] {
  if (typeof entry === "function") {
    const result = entry();
    // Only add non-empty strings from functions
    if (result) {
      acc.push(result);
    }
  } else if (typeof entry === "string" && entry) {
    // Only add non-empty strings
    acc.push(entry);
  } else if (entry === true) {
    devWarn("classNames: true is not a valid class name");
  }

  return acc;
}
